// Rate limiting and DoS protection
interface RateLimitRecord {
  count: number
  resetTime: number
  lastAccess: number
}

class RateLimiter {
  private static limits = new Map<string, RateLimitRecord>()
  private static cleanupInterval: NodeJS.Timeout | null = null

  // Initialize cleanup interval
  static init(): void {
    if (this.cleanupInterval) return

    this.cleanupInterval = setInterval(() => {
      this.cleanup()
    }, 60000) // Cleanup every minute
  }

  // Check if action is rate limited
  static isAllowed(
    identifier: string, 
    maxRequests: number = 5, 
    windowMs: number = 60000
  ): { allowed: boolean; remaining: number; resetTime: number } {
    const now = Date.now()
    const record = this.limits.get(identifier)

    if (!record || now > record.resetTime) {
      // New window or expired
      const newRecord: RateLimitRecord = {
        count: 1,
        resetTime: now + windowMs,
        lastAccess: now
      }
      this.limits.set(identifier, newRecord)
      return { allowed: true, remaining: maxRequests - 1, resetTime: newRecord.resetTime }
    }

    // Update existing record
    record.lastAccess = now

    if (record.count >= maxRequests) {
      return { allowed: false, remaining: 0, resetTime: record.resetTime }
    }

    record.count++
    return { 
      allowed: true, 
      remaining: maxRequests - record.count, 
      resetTime: record.resetTime 
    }
  }

  // Get rate limit status
  static getStatus(identifier: string): { count: number; remaining: number; resetTime: number } | null {
    const record = this.limits.get(identifier)
    if (!record) return null

    const now = Date.now()
    if (now > record.resetTime) return null

    return {
      count: record.count,
      remaining: Math.max(0, 5 - record.count), // Default max of 5
      resetTime: record.resetTime
    }
  }

  // Reset rate limit for identifier
  static reset(identifier: string): void {
    this.limits.delete(identifier)
  }

  // Cleanup expired records
  private static cleanup(): void {
    const now = Date.now()
    const expiredKeys: string[] = []

    for (const [key, record] of this.limits.entries()) {
      if (now > record.resetTime) {
        expiredKeys.push(key)
      }
    }

    expiredKeys.forEach(key => this.limits.delete(key))
  }

  // Get all active limits (for monitoring)
  static getActiveLimits(): Array<{ identifier: string; record: RateLimitRecord }> {
    const now = Date.now()
    const active: Array<{ identifier: string; record: RateLimitRecord }> = []

    for (const [identifier, record] of this.limits.entries()) {
      if (now <= record.resetTime) {
        active.push({ identifier, record })
      }
    }

    return active
  }

  // Detect potential DoS attack
  static detectSuspiciousActivity(): string[] {
    const now = Date.now()
    const suspicious: string[] = []

    for (const [identifier, record] of this.limits.entries()) {
      // High request rate
      if (record.count > 20) {
        suspicious.push(identifier)
      }

      // Multiple windows in short time
      const recentWindows = this.getRecentWindows(identifier, now, 300000) // 5 minutes
      if (recentWindows > 5) {
        suspicious.push(identifier)
      }
    }

    return suspicious
  }

  private static getRecentWindows(identifier: string, now: number, timeWindow: number): number {
    // This would require storing more history in a real implementation
    // For now, just return the current count as a simple heuristic
    const record = this.limits.get(identifier)
    return record && (now - record.lastAccess) < timeWindow ? record.count : 0
  }
}

// Initialize rate limiter
RateLimiter.init()

export default RateLimiter
